import React, { useEffect, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, updateUserProfile } from '../firebase';
import {
  getPermissionState,
  isPushSupported,
  enablePushNotifications,
} from '../utils/notificationService';
import { usePushNotificationsEnabled } from '../hooks/useFeatureFlag';
import './NotificationOptInBanner.css';

/**
 * Contextual opt-in banner shown during processing wait states (analyze
 * or generate). Quietly hides itself when:
 *   - the browser does not support push
 *   - the user has already enabled push for this event
 *   - the user is signed out
 *
 * Props:
 *   event: 'analysis_complete' | 'generation_complete'
 */
function NotificationOptInBanner({ event }) {
  const user = auth.currentUser;
  const { enabled: pushNotificationsEnabled } = usePushNotificationsEnabled();
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(getPermissionState());
  const [enabledForEvent, setEnabledForEvent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [justOptedIn, setJustOptedIn] = useState(false);
  const currentPrefsRef = useRef({ enabled: false, events: [] });

  useEffect(() => {
    let cancelled = false;
    isPushSupported().then((ok) => { if (!cancelled) setSupported(ok); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const userRef = doc(db, 'users', user.uid);
    const unsub = onSnapshot(userRef, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      const prefs = (data.notificationPreferences || {}).push || {};
      const events = Array.isArray(prefs.events) ? prefs.events : [];
      currentPrefsRef.current = { enabled: Boolean(prefs.enabled), events };
      setEnabledForEvent(Boolean(prefs.enabled) && events.includes(event));
    });
    return () => unsub();
  }, [user, event]);

  if (!pushNotificationsEnabled) return null;
  if (!user || !supported) return null;
  if (permission === 'denied') {
    return (
      <div className="notif-banner notif-banner--muted" data-testid="notif-banner-denied">
        Notifications are blocked in your browser settings.
      </div>
    );
  }

  if (enabledForEvent || justOptedIn) {
    return (
      <div className="notif-banner notif-banner--confirmed" data-testid="notif-banner-confirmed">
        <svg className="notif-banner-check" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>You'll be notified when it's ready — feel free to close this tab.</span>
      </div>
    );
  }

  const copy = event === 'analysis_complete'
    ? 'Analysis can take a minute. Want to be notified when it\u2019s done?'
    : 'Generation may take a few minutes. Enable notifications so you don\u2019t have to wait.';

  const handleOptIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await enablePushNotifications();
      setPermission(getPermissionState());
      if (result.ok) {
        // Write notification preferences to Firestore so the backend
        // trigger knows this user wants push for this event.  Merge with
        // any existing events so we don't clobber other subscriptions.
        const existing = currentPrefsRef.current;
        const mergedEvents = existing.events.includes(event)
          ? existing.events
          : [...existing.events, event];
        await updateUserProfile({
          notificationPreferences: {
            push: { enabled: true, events: mergedEvents },
          },
        });
        setJustOptedIn(true);
      } else if (result.reason === 'denied') {
        setError('Permission was denied.');
      } else if (result.reason === 'no-vapid') {
        setError('Push notifications are not configured for this environment.');
      } else if (result.reason === 'unsupported') {
        setSupported(false);
      } else {
        setError('Could not enable notifications. Please try again.');
      }
    } catch (e) {
      setError('Could not enable notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="notif-banner" data-testid={`notif-banner-${event}`}>
      <span className="notif-banner-text">{copy}</span>
      <button
        type="button"
        className="notif-banner-btn"
        onClick={handleOptIn}
        disabled={busy}
        data-testid="notif-banner-optin-btn"
      >
        {busy ? 'Enabling…' : 'Notify me'}
      </button>
      {error && <span className="notif-banner-error" role="alert">{error}</span>}
    </div>
  );
}

export default NotificationOptInBanner;
